<template>
    <span class="inline-citations" :class="`inline-citations--${variant}`">
        <template v-for="(part, idx) in parts" :key="idx">
            <a
                v-if="part.cite"
                class="inline-cite"
                :class="`inline-cite--${variant}`"
                :href="part.url"
                target="_blank"
                rel="noopener noreferrer"
                :title="citeTitle(part)"
                :aria-label="citeAria(part)"
            >
                <span class="inline-cite-num">{{ part.index }}</span>
            </a>
            <strong v-else-if="part.bold">{{ part.text }}</strong>
            <span v-else>{{ part.text }}</span>
        </template>
    </span>
</template>

<script setup>
import { computed } from 'vue'
import { parseInlineCitations } from '../utils/parseInlineCitations.js'

const props = defineProps({
    text: { type: String, default: '' },
    sources: { type: Array, default: () => [] },
    /** default — основной текст; callout — блок «источники» на розовом фоне */
    variant: {
        type: String,
        default: 'default',
        validator: (v) => ['default', 'callout'].includes(v),
    },
})

const parts = computed(() => parseInlineCitations(props.text, props.sources))

function citeTitle(part) {
    const title = (part.title || '').trim()
    if (title && title !== String(part.url)) return title
    try {
        return new URL(part.url).hostname.replace(/^www\./, '')
    } catch {
        return part.url || ''
    }
}

function citeAria(part) {
    return `Источник ${part.index}: ${citeTitle(part)}`
}
</script>

<style scoped>
.inline-citations {
    display: inline;
}

/* Общая плашка-сноска */
.inline-cite {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.15rem;
    height: 1.05rem;
    padding: 0 0.35rem;
    margin: 0 0.12rem;
    vertical-align: 0.08em;
    border-radius: 4px;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.01em;
    text-decoration: none;
    white-space: nowrap;
    transition:
        background-color 0.14s ease,
        border-color 0.14s ease,
        color 0.14s ease,
        box-shadow 0.14s ease,
        transform 0.12s ease;
}

.inline-cite-num {
    display: block;
    font-variant-numeric: tabular-nums;
}

/* Основной текст ответа */
.inline-cite--default {
    color: #9B1C22;
    background: #FFF5F5;
    border: 1px solid #F0D4D6;
    box-shadow: 0 1px 0 rgba(227, 6, 19, 0.06);
}

.inline-cite--default:hover {
    color: #FFFFFF;
    background: #E30613;
    border-color: #E30613;
    box-shadow: 0 2px 6px rgba(227, 6, 19, 0.22);
    transform: translateY(-0.5px);
}

.inline-cite--default:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(227, 6, 19, 0.18);
}

/* Callout на розовом фоне */
.inline-cite--callout {
    color: #B4181E;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(227, 6, 19, 0.22);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}

.inline-cite--callout:hover {
    color: #FFFFFF;
    background: #E30613;
    border-color: #C41E24;
    box-shadow: 0 2px 8px rgba(180, 24, 30, 0.28);
    transform: translateY(-0.5px);
}

.inline-cite--callout:active {
    transform: translateY(0);
}

.inline-citations--callout strong {
    font-weight: 600;
}
</style>
